import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import styles from "./OrderComplete.module.css";
import ProductImage from "../../components/common/ProductImage/ProductImage";
import { clearCart } from "../../redux/cartReducer";

// **UPDATED: Enhanced interfaces to match the new checkout response**
interface EnhancedOrderTotals {
  originalSubtotal: number;
  customizationTotal: number;
  promotionalDiscountTotal: number;
  couponDiscountTotal: number;
  deliveryFeeTotal: number;
  finalSubtotal: number;
  grandTotal: number;
}

interface AppliedPromotion {
  id: number;
  name: string;
  description: string;
  totalDiscount: number;
}

interface AppliedCoupon {
  code: string;
  description: string;
  totalDiscount: number;
}

// **NEW: Shipping information interfaces**
interface UserAddress {
  id?: number;
  street?: string;
  streetNumber?: string;
  district?: string;
  reference?: string;
  zipCode?: string;
  city?: string;
  state?: string;
  country?: string;
}

interface ReceiverInfo {
  name?: string;
  phone?: string;
  documentType?: string;
  documentNumber?: string;
}

interface CustomerInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  documentType?: string;
  documentNumber?: string;
}

interface ProductPricing {
  originalUnitPrice: number;
  originalTotal: number;
  customizationSurcharge: number;
  customizationTotal: number;
  discounts: {
    promotional?: {
      description: string;
      amountPerUnit: number;
      totalAmount: number;
      promotionId: number;
    };
    coupon?: {
      code: string;
      description: string;
      amountPerUnit: number;
      totalAmount: number;
    };
  };
  finalUnitPrice: number;
  finalTotal: number;
}

interface EnhancedProduct {
  id: number;
  quantity: number;
  pricing: ProductPricing;
  product: {
    id: number;
    name: string;
    images: {
      thumbnail: string;
      small: string;
      medium?: string;
    }[];
  };
}

interface GroupTotals {
  originalSubtotal: number;
  customizationTotal: number;
  promotionalDiscountTotal: number;
  couponDiscountTotal: number;
  finalSubtotal: number;
  deliveryFeeTotal: number;
  groupTotal: number;
}

interface EnhancedGroup {
  id: number;
  deliveryMethod: string;
  deliveryDate: string;
  deliveryFee: number;
  groupTotals: GroupTotals;
  items: EnhancedProduct[];
}

// **NEW: Enhanced Order State that matches the checkout response**
interface EnhancedOrderState {
  orderId: string;
  generatedOrderId: string;
  createdAt: string;
  customerName: string;
  email: string;
  paymentMethod: "yapePlin" | "money_transfer" | "payme";
  paymentStatus: "paid" | "pending";
  orderStatus: "processing" | "shipped" | "delivered";

  // **NEW: Shipping information**
  user_address?: UserAddress;
  receiverInfo?: ReceiverInfo;
  customerInfo?: CustomerInfo;

  // Enhanced pricing from CleanCartCalculationService
  totals: EnhancedOrderTotals;
  appliedCoupons: AppliedCoupon[];
  appliedPromotions: AppliedPromotion[];

  // Legacy fields for backward compatibility
  totalPrice?: number;
  totalDeliveryFee?: number;
  finalTotal?: number;

  groups: EnhancedGroup[];
}

// Legacy interfaces for backward compatibility
interface LegacyProduct {
  id: number;
  quantity: number;
  price: number;
  finalPrice: number;
  product: {
    id: number;
    name: string;
    images: {
      thumbnail: string;
      small: string;
      medium: string;
    }[];
  };
}

interface LegacyGroup {
  id: number;
  subTotal: number;
  deliveryMethod: string;
  deliveryDate: string;
  deliveryFee: number;
  groupTotal: number;
  items: LegacyProduct[];
}

interface LegacyOrderState {
  orderId: number;
  generatedOrderId: string;
  createdAt: string;
  email: string;
  customerName: string;
  paymentStatus: "paid" | "pending";
  paymentMethod: "yapePlin" | "money_transfer" | "payme";
  totalPrice: number;
  totalDeliveryFee: number;
  finalTotal: number;
  groups: LegacyGroup[];
  orderStatus: "processing" | "shipped" | "delivered";
}

// **HELPER: Check if order has enhanced pricing structure**
function hasEnhancedPricing(order: any): order is EnhancedOrderState {
  return (
    order &&
    typeof order.totals === "object" &&
    typeof order.totals.grandTotal === "number" &&
    Array.isArray(order.groups) &&
    order.groups.length > 0 &&
    order.groups[0].groupTotals &&
    Array.isArray(order.groups[0].items) &&
    order.groups[0].items.length > 0 &&
    order.groups[0].items[0].pricing
  );
}

// **HELPER: Transform legacy to enhanced format**
function transformLegacyToEnhanced(
  legacy: LegacyOrderState
): EnhancedOrderState {
  const totalDiscounts = 0; // Legacy orders don't have proper discount breakdown

  return {
    orderId: legacy.orderId.toString(),
    generatedOrderId: legacy.generatedOrderId,
    createdAt: legacy.createdAt,
    customerName: legacy.customerName,
    email: legacy.email,
    paymentMethod: legacy.paymentMethod,
    paymentStatus: legacy.paymentStatus,
    orderStatus: legacy.orderStatus,

    totals: {
      originalSubtotal: legacy.totalPrice + totalDiscounts,
      customizationTotal: 0,
      promotionalDiscountTotal: 0,
      couponDiscountTotal: 0,
      deliveryFeeTotal: legacy.totalDeliveryFee,
      finalSubtotal: legacy.totalPrice,
      grandTotal: legacy.finalTotal,
    },

    appliedCoupons: [],
    appliedPromotions: [],

    // Keep legacy fields
    totalPrice: legacy.totalPrice,
    totalDeliveryFee: legacy.totalDeliveryFee,
    finalTotal: legacy.finalTotal,

    groups: legacy.groups.map((group) => ({
      id: group.id,
      deliveryMethod: group.deliveryMethod,
      deliveryDate: group.deliveryDate,
      deliveryFee: group.deliveryFee,
      groupTotals: {
        originalSubtotal: group.subTotal,
        customizationTotal: 0,
        promotionalDiscountTotal: 0,
        couponDiscountTotal: 0,
        finalSubtotal: group.subTotal,
        deliveryFeeTotal: group.deliveryFee,
        groupTotal: group.groupTotal,
      },
      items: group.items.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        pricing: {
          originalUnitPrice: item.price,
          originalTotal: item.price * item.quantity,
          customizationSurcharge: 0,
          customizationTotal: 0,
          discounts: {},
          finalUnitPrice: item.finalPrice / item.quantity,
          finalTotal: item.finalPrice,
        },
        product: item.product,
      })),
    })),
  };
}

const steps = [
  "Pendiente de Pago",
  "Confirmado",
  "Preparando",
  "En camino",
  "Entregado",
];

const OrderComplete: React.FC = () => {
  const dispatch = useDispatch();
  const { state } = useLocation();

  // Try to get order data from navigation state, fallback to sessionStorage
  let rawOrderState = state as EnhancedOrderState | LegacyOrderState | null;

  if (!rawOrderState) {
    const stored = sessionStorage.getItem("orderSuccessData");
    if (stored) {
      try {
        rawOrderState = JSON.parse(stored);
      } catch {
        rawOrderState = null;
      }
    }
  }

  // **FIXED: Transform to enhanced format if needed**
  const orderState: EnhancedOrderState | null = rawOrderState
    ? hasEnhancedPricing(rawOrderState)
      ? (rawOrderState as EnhancedOrderState)
      : transformLegacyToEnhanced(rawOrderState as LegacyOrderState)
    : null;

  // Always scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Always call useEffect at the top level
  useEffect(() => {
    if (orderState) {
      dispatch(clearCart());
      localStorage.removeItem("cartId");

      // FIXED: Also clear the cartId cookie to prevent conflicts with new carts
      // Clear cookie for both development and production domains
      const isDevelopment = process.env.NODE_ENV !== "production";
      const domain = isDevelopment
        ? undefined
        : `.${process.env.REACT_APP_BASE_DOMAIN || "evefant.com"}`;

      // Clear cookie with the same options used when setting it
      document.cookie = `cartId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${
        domain ? `domain=${domain};` : ""
      } SameSite=lax`;

      // Also try clearing without domain for local development
      if (isDevelopment) {
        document.cookie = `cartId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=lax`;
      }

      console.log("✅ Cart data cleared: localStorage and cookies cleaned");
    }
  }, [dispatch, orderState]);

  // If still no orderState, show error or redirect
  if (!orderState) {
    return (
      <div className={styles.container}>
        <div className={styles.headerWrapper}>
          <h1>Orden no encontrada</h1>
          <p>
            No se encontró información de la orden. Por favor, revisa tu correo
            o vuelve a intentarlo desde el historial de pedidos.
          </p>
        </div>
      </div>
    );
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDeliveryMethod = (method: string) => {
    switch (method.toLowerCase()) {
      case "standard":
        return "Standard";
      case "rapido":
        return "Rápido";
      case "express":
        return "Express";
      default:
        return method;
    }
  };

  const formatDate = (date: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    };
    return new Date(date).toLocaleDateString("es-ES", options);
  };

  const getActiveStepIndex = () => {
    // Special logic for card payments ("payme")
    if (orderState.paymentMethod === "payme") {
      if (orderState.paymentStatus === "pending") return 0; // "Pendiente de Pago"
      if (orderState.paymentStatus === "paid") return 2; // "Preparando"
      if (orderState.orderStatus === "shipped") return 3;
      if (orderState.orderStatus === "delivered") return 4;
      return 2; // Default to "Preparando"
    }
    // Default logic for other payment methods
    if (orderState.paymentStatus === "pending") return 0;
    if (orderState.paymentStatus === "paid") return 1;
    if (orderState.orderStatus === "shipped") return 3;
    if (orderState.orderStatus === "delivered") return 4;
    return 2; // "Preparando" is default after paid
  };

  const renderStepper = () => {
    const activeStepIndex = getActiveStepIndex();

    return (
      <div className={styles.stepper}>
        {steps.map((step, index) => {
          const isCompleted = index < activeStepIndex;
          const isActive = index === activeStepIndex;

          return (
            <div key={index} className={styles.step}>
              <div
                className={`${styles.circle} ${
                  isCompleted ? styles.completed : ""
                } ${isActive ? styles.active : ""}`}
              >
                {index + 1}
              </div>
              <div className={styles.stepLabel}>{step}</div>
              {index !== steps.length - 1 && (
                <div
                  className={`${styles.connector} ${
                    isCompleted ? styles.completedConnector : ""
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPaymentInstructions = () => {
    if (orderState.paymentMethod === "yapePlin") {
      return (
        <div className={styles.paymentInstructions}>
          <h3>Instrucciones de Pago - Yape / Plin</h3>
          <div className={styles.qrSection}>
            <p>Escanea el código QR para realizar el pago</p>
            <img
              src={`${process.env.REACT_APP_IMAGE_FOLDER}/evefant_yape_QR_b0bef0eaad.webp`}
              alt="Yape QR Code"
              className={styles.qrCode}
            />
            <div className={styles.accountDetail}>
              <span className={styles.accountLabel}>Celular:</span>
              <span className={styles.accountValue}>906 869 576</span>
              <button
                className={styles.copyButton}
                onClick={() => handleCopy("906869576")}
                title="Copiar número"
              >
                <ContentCopyIcon />
              </button>
            </div>
            <p>Titular: Evefant SAC</p>
          </div>
          {renderContactSection()}
        </div>
      );
    }

    if (orderState.paymentMethod === "money_transfer") {
      return (
        <div className={styles.paymentInstructions}>
          <h3>Instrucciones de Pago - Transferencia Bancaria</h3>
          <div className={styles.bankDetails}>
            <div className={styles.bankHeader}>
              <img
                src={`${process.env.REACT_APP_IMAGE_FOLDER}/Logo_bcp_vector_58cf4828f2.svg`}
                alt="BCP"
                className={styles.bankLogo}
              />
              <h4>Banco de Crédito BCP</h4>
            </div>
            <div className={styles.accountDetail}>
              <span className={styles.accountLabel}>Nro. Cuenta:</span>
              <div className={styles.accountValueWrapper}>
                <span className={styles.accountValue}>194-9074-6492-0-20</span>
                <button
                  className={styles.copyButton}
                  onClick={() => handleCopy("19490746492020")}
                  title="Copiar número de cuenta"
                >
                  <ContentCopyIcon />
                </button>
              </div>
            </div>
            <div className={styles.accountDetail}>
              <span className={styles.accountLabel}>CCI:</span>
              <div className={styles.accountValueWrapper}>
                <span className={styles.accountValue}>
                  002-194-19074-6492-0-2091
                </span>
                <button
                  className={styles.copyButton}
                  onClick={() => handleCopy("00219419074649202091")}
                  title="Copiar CCI"
                >
                  <ContentCopyIcon />
                </button>
              </div>
            </div>
            <p>Titular: Evefant SAC - Cuenta Corriente</p>
          </div>
          {renderContactSection()}
        </div>
      );
    }
    return null;
  };

  const renderContactSection = () => (
    <div className={styles.contactSection}>
      <p className={styles.contactTitle}>
        Una vez realizado el pago, envíanos el comprobante por cualquiera de
        estos medios:
      </p>
      <div className={styles.contactMethods}>
        <div className={styles.contactMethod}>
          <span className={styles.contactLabel}>
            <WhatsAppIcon /> WhatsApp (906 869 576)
          </span>
          <button
            className={styles.copyButton}
            onClick={() => handleCopy("906869576")}
            title="Copiar número"
          >
            <ContentCopyIcon />
          </button>
        </div>
        <div className={styles.contactMethod}>
          <span className={styles.contactLabel}>
            <EmailIcon /> Email (ventas@evefant.com)
          </span>
          <button
            className={styles.copyButton}
            onClick={() => handleCopy("ventas@evefant.com")}
            title="Copiar email"
          >
            <ContentCopyIcon />
          </button>
        </div>
      </div>
    </div>
  );

  const renderGroupSummary = (group: EnhancedGroup, index: number) => (
    <div key={group.id} className={styles.groupSummary}>
      <div className={styles.groupHeader}>
        <h3>
          {`Paquete ${index + 1} - ${formatDeliveryMethod(
            group.deliveryMethod
          )} - 
          ${formatDate(group.deliveryDate)}`}
        </h3>
      </div>

      <div className={styles.groupContent}>
        {group.items.map((product) => (
          <div key={product.id} className={styles.product}>
            <ProductImage
              src={`${product.product.images[0]?.thumbnail}`}
              alt={product.product.name}
              className={styles.productImage}
            />
            <div className={styles.productInfo}>
              <span className={styles.productName}>{product.product.name}</span>

              <span className={styles.productDetails}>
                Cantidad: {product.quantity} x S/{" "}
                {product.pricing.originalUnitPrice.toFixed(2)}
              </span>

              {/* Display promotional discount if present */}
              {product.pricing.discounts?.promotional &&
                product.pricing.discounts.promotional.totalAmount > 0 && (
                  <span className={styles.discountDetails}>
                    - {product.pricing.discounts.promotional.description}: S/{" "}
                    {product.pricing.discounts.promotional.totalAmount.toFixed(
                      2
                    )}
                  </span>
                )}

              {/* Display coupon discount if present */}
              {product.pricing.discounts?.coupon &&
                product.pricing.discounts.coupon.totalAmount > 0 && (
                  <span className={styles.discountDetails}>
                    - Cupón {product.pricing.discounts.coupon.code}: S/{" "}
                    {product.pricing.discounts.coupon.totalAmount.toFixed(2)}
                  </span>
                )}
            </div>
            <div className={styles.productPricing}>
              <span className={styles.finalPrice}>
                S/ {product.pricing.finalTotal.toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.groupFooter}>
        <div className={styles.footerRow}>
          <span>Subtotal original:</span>
          <span>S/ {group.groupTotals.originalSubtotal.toFixed(2)}</span>
        </div>

        {/* Display promotional discounts if present */}
        {group.groupTotals.promotionalDiscountTotal > 0 && (
          <div className={styles.footerRow}>
            <span>Descuentos Promocionales:</span>
            <span className={styles.discountAmount}>
              -S/ {group.groupTotals.promotionalDiscountTotal.toFixed(2)}
            </span>
          </div>
        )}

        {/* Display coupon discounts if present */}
        {group.groupTotals.couponDiscountTotal > 0 && (
          <div className={styles.footerRow}>
            <span>Descuentos por Cupón:</span>
            <span className={styles.discountAmount}>
              -S/ {group.groupTotals.couponDiscountTotal.toFixed(2)}
            </span>
          </div>
        )}

        <div className={styles.footerRow}>
          <span>Subtotal Final:</span>
          <span>S/ {group.groupTotals.finalSubtotal.toFixed(2)}</span>
        </div>
        <div className={styles.footerRow}>
          <span>Costo de Envío:</span>
          <span>S/ {group.groupTotals.deliveryFeeTotal.toFixed(2)}</span>
        </div>
        <div className={styles.footerRow}>
          <span>
            <strong>Total del Paquete:</strong>
          </span>
          <span>
            <strong>S/ {group.groupTotals.groupTotal.toFixed(2)}</strong>
          </span>
        </div>
      </div>
    </div>
  );

  // **NEW: Helper function to format complete address**
  const formatCompleteAddress = (address: any): string => {
    if (!address) return "No especificado";

    const addressParts = [];

    // Add street and street number
    if (address.street) {
      let streetInfo = address.street;
      if (address.streetNumber) {
        streetInfo += ` ${address.streetNumber}`;
      }
      addressParts.push(streetInfo);
    }

    // Add district
    if (address.district) {
      addressParts.push(address.district);
    }

    // Add province (if different from district)
    if (address.province && address.province !== address.district) {
      addressParts.push(address.province);
    }

    // Add department
    if (address.department) {
      addressParts.push(address.department);
    }

    // Add country
    if (address.country) {
      addressParts.push(address.country);
    }

    return addressParts.length > 0
      ? addressParts.join(", ")
      : "No especificado";
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div className={styles.headerSection}>
          <div className={styles.successIcon}>
            <img
              src="https://media.evefant.com/green_check_icon_evf_43f85b4028.svg"
              alt="Success Icon"
              width="24"
              height="24"
            />
          </div>
          <h1>¡Orden Confirmada!</h1>
        </div>
        <div className={styles.divider} />
        <div className={styles.notificationSection}>
          <div className={styles.notificationInfo}>
            <h3>
              Enviaremos los cambios de estado de tu orden a{" "}
              <strong>{orderState.email || "tu correo electrónico"}</strong>
            </h3>
          </div>
          {renderStepper()}
        </div>
      </div>

      <div className={styles.orderBasicInfo}>
        <div className={styles.card}>
          <p className={styles.orderNumber}>
            Orden #<strong>{orderState.generatedOrderId}</strong>
          </p>
          <p className={styles.customerName}>
            Cliente: <strong>{orderState.customerName}</strong>
          </p>
          <p className={styles.paymentMethod}>
            Método de Pago:{" "}
            <strong>
              {orderState.paymentMethod === "yapePlin"
                ? "Yape / Plin"
                : orderState.paymentMethod === "money_transfer"
                ? "Transferencia Bancaria"
                : "Tarjeta de Crédito/Débito"}
            </strong>
          </p>
          <p className={styles.paymentStatus}>
            Estado de Pago:{" "}
            <span
              className={`${styles.statusBadge} ${
                orderState.paymentStatus === "paid"
                  ? styles.statusPaid
                  : styles.statusPending
              }`}
            >
              {orderState.paymentStatus === "paid" ? "Pagado" : "Pendiente"}
            </span>
          </p>
          <p className={styles.totalAmount}>
            Total a Pagar:{" "}
            <strong className={styles.totalAmountHighlight}>
              S/ {orderState.totals.grandTotal.toFixed(2)}
            </strong>
          </p>
        </div>

        {/* **NEW: Shipping Information Card** */}
        <div className={styles.card}>
          <h3>Información de Envío</h3>
          <div className={styles.shippingInfo}>
            <p>
              <strong>Destinatario:</strong>{" "}
              {orderState.receiverInfo?.name || orderState.customerName}
            </p>
            <p>
              <strong>Teléfono:</strong>{" "}
              {orderState.receiverInfo?.phone ||
                orderState.customerInfo?.phone ||
                "No especificado"}
            </p>
            {/* **FIXED: Single Dirección field with complete address** */}
            <p>
              <strong>Dirección:</strong>{" "}
              {formatCompleteAddress(orderState.user_address)}
            </p>
            {/* **NEW: Reference as separate field if available** */}
            {orderState.user_address?.reference && (
              <p>
                <strong>Referencia:</strong> {orderState.user_address.reference}
              </p>
            )}
          </div>
        </div>

        {orderState.paymentStatus === "pending" && (
          <div className={styles.card}>{renderPaymentInstructions()}</div>
        )}
      </div>

      <div className={styles.orderSummary}>
        <h2>Detalles de la Orden</h2>
        <div className={styles.groupsContainer}>
          {orderState.groups.map((group, index) =>
            renderGroupSummary(group, index)
          )}
        </div>

        <div className={styles.orderTotals}>
          <div className={styles.totalRow}>
            <span>Subtotal de Productos:</span>
            <span>S/ {orderState.totals.originalSubtotal.toFixed(2)}</span>
          </div>

          {/* Display promotional discounts if present */}
          {orderState.totals.promotionalDiscountTotal > 0 && (
            <div className={styles.totalRow}>
              <span>Descuentos Promocionales:</span>
              <span className={styles.discountAmount}>
                -S/ {orderState.totals.promotionalDiscountTotal.toFixed(2)}
              </span>
            </div>
          )}

          {/* Display coupon discounts if present */}
          {orderState.totals.couponDiscountTotal > 0 && (
            <div className={styles.totalRow}>
              <span>Descuentos por Cupón:</span>
              <span className={styles.discountAmount}>
                -S/ {orderState.totals.couponDiscountTotal.toFixed(2)}
              </span>
            </div>
          )}

          <div className={styles.totalRow}>
            <span>Subtotal Final:</span>
            <span>S/ {orderState.totals.finalSubtotal.toFixed(2)}</span>
          </div>
          <div className={styles.totalRow}>
            <span>Total de Envío:</span>
            <span>S/ {orderState.totals.deliveryFeeTotal.toFixed(2)}</span>
          </div>
          <div className={`${styles.totalRow} ${styles.finalTotal}`}>
            <span>
              <strong>Total a Pagar:</strong>
            </span>
            <span>
              <strong>S/ {orderState.totals.grandTotal.toFixed(2)}</strong>
            </span>
          </div>
        </div>

        {/* **NEW: Separate promotions detail section below totals** */}
        {(orderState.appliedCoupons && orderState.appliedCoupons.length > 0) ||
        (orderState.appliedPromotions &&
          orderState.appliedPromotions.length > 0) ? (
          <div className={styles.promotionsDetailSection}>
            <h3>Detalle de Descuentos Aplicados</h3>

            {/* Display applied coupons */}
            {orderState.appliedCoupons &&
              orderState.appliedCoupons.length > 0 && (
                <div className={styles.appliedCoupons}>
                  <h4>Cupones Aplicados:</h4>
                  {orderState.appliedCoupons.map((coupon, index) => (
                    <div key={index} className={styles.promotionDetailItem}>
                      <span>
                        {coupon.code} - {coupon.description}
                      </span>
                      <span className={styles.discountAmount}>
                        -S/ {coupon.totalDiscount.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

            {/* Display applied promotions */}
            {orderState.appliedPromotions &&
              orderState.appliedPromotions.length > 0 && (
                <div className={styles.appliedPromotions}>
                  <h4>Promociones Aplicadas:</h4>
                  {orderState.appliedPromotions.map((promotion, index) => (
                    <div key={index} className={styles.promotionDetailItem}>
                      <span>
                        {promotion.name} - {promotion.description}
                      </span>
                      <span className={styles.discountAmount}>
                        -S/ {promotion.totalDiscount.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OrderComplete;
